import { useState, useEffect, useRef } from 'react'
import { Camera, Image as ImageIcon, Edit3 } from 'lucide-react'
import { CATEGORIES, BLOCK_TYPES } from '../../db/database'
import { Icon, CategoryIcon } from '../../ui/icons'
import { useT, useLang } from '../../i18n/useT'
import { useFeature } from '../../config/features'
import useTaskStore from '../../store/taskStore'
import useAuthStore from '../../store/authStore'
import useSmartListStore from '../../store/smartListStore'
import { stripPastDate } from './helpers'
import ManualForm from './ManualForm'
import BloccoForm, { partsToTs } from '../BloccoForm'

async function compressImage(file) {
  // Azure OpenAI Vision con detail:'high' processa nativamente fino a 2048px
  // sul lato lungo. Stare a 1120 comprime troppo e le celle più lontane dal
  // centro diventano illeggibili (tipico per tabelle-turno fitte).
  const MAX = 2048

  // createImageBitmap: much more memory-efficient on mobile
  if (typeof createImageBitmap === 'function') {
    const bitmap = await createImageBitmap(file)
    let { width, height } = bitmap
    if (width > MAX || height > MAX) {
      if (width > height) { height = Math.round(height * MAX / width); width = MAX }
      else                { width  = Math.round(width  * MAX / height); height = MAX }
    }
    const canvas = new OffscreenCanvas(width, height)
    canvas.getContext('2d').drawImage(bitmap, 0, 0, width, height)
    bitmap.close()
    const blob = await canvas.convertToBlob({ type: 'image/jpeg', quality: 0.85 })
    const buffer = await blob.arrayBuffer()
    const bytes = new Uint8Array(buffer)
    let binary = ''
    for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i])
    return btoa(binary)
  }

  // Fallback for older browsers (Safari < 15 etc.)
  return new Promise((resolve, reject) => {
    const timeout = setTimeout(() => reject(new Error('compress_timeout')), 15000)
    const reader = new FileReader()
    reader.onerror = () => { clearTimeout(timeout); reject(new Error('read_error')) }
    reader.onload  = (e) => {
      const img = new Image()
      img.onerror = () => { clearTimeout(timeout); reject(new Error('img_error')) }
      img.onload  = () => {
        clearTimeout(timeout)
        let { width, height } = img
        if (width > MAX || height > MAX) {
          if (width > height) { height = Math.round(height * MAX / width); width = MAX }
          else                { width  = Math.round(width  * MAX / height); height = MAX }
        }
        const canvas = document.createElement('canvas')
        canvas.width  = width
        canvas.height = height
        canvas.getContext('2d').drawImage(img, 0, 0, width, height)
        resolve(canvas.toDataURL('image/jpeg', 0.85).split(',')[1])
      }
      img.src = e.target.result
    }
    reader.readAsDataURL(file)
  })
}

export default function ImageMode({ onSave, onClose, backHandlerRef }) {
  const t                = useT()
  const lang             = useLang()
  const can              = useFeature()
  const openPremiumModal = useTaskStore(s => s.openPremiumModal)
  const authLoading      = useAuthStore(s => s.loading)

  useEffect(() => {
    if (authLoading) return
    if (!can('image_input')) { openPremiumModal(); onClose() }
  }, [authLoading]) // eslint-disable-line

  const [phase,    setPhase]    = useState('upload')
  const [results,  setResults]  = useState([])   // array di task O di blocchi
  const [resultKind, setResultKind] = useState('tasks') // 'tasks' | 'blocks'
  const [selected, setSelected] = useState(new Set())
  const [preview,  setPreview]  = useState(null)
  const [summary,  setSummary]  = useState('')
  const [errorMsg, setErrorMsg] = useState('')
  const [saving,   setSaving]   = useState(false)
  const [editingIndex, setEditingIndex] = useState(null)
  const [blocksMode, setBlocksMode] = useState(false)   // era "shiftsMode"
  const addBlocco = useTaskStore(s => s.addBlocco)
  const inputRef  = useRef(null)
  const cameraRef = useRef(null)

  // Register back handler: if in inline edit, cancel it instead of closing modal
  useEffect(() => {
    if (!backHandlerRef) return
    backHandlerRef.current = () => {
      if (editingIndex !== null) {
        setEditingIndex(null)
        return true // handled
      }
      return false
    }
    return () => { if (backHandlerRef.current) backHandlerRef.current = null }
  }, [editingIndex, backHandlerRef])

  const analyzeFile = async (file) => {
    if (!file || !file.type.startsWith('image/')) return
    setPreview(URL.createObjectURL(file))
    setPhase('analyzing')
    try {
      const imageBase64 = await compressImage(file)
      const token = useAuthStore.getState().session?.access_token
      if (!token) throw new Error('auth')

      const res  = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/image-to-task`,
        {
          method:  'POST',
          headers: {
            'Content-Type':  'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({ image: imageBase64, mimeType: 'image/jpeg', mode: blocksMode ? 'blocks' : 'standard' }),
        }
      )
      const json = await res.json()

      if (res.status === 403) { openPremiumModal(); onClose(); return }
      if (!res.ok)             throw new Error(json.error || 'server')

      // In base al mode il backend ritorna `tasks[]` oppure `blocks[]`.
      // Richiediamo SEMPRE coerenza col mode richiesto: se abbiamo chiesto
      // blocks ma il server ha risposto con tasks (o viceversa), significa
      // che sta girando una versione della function non allineata col client.
      const expectedKind = blocksMode ? 'blocks' : 'tasks'
      const serverKind   = json.mode === 'blocks' ? 'blocks' : 'tasks'
      if (serverKind !== expectedKind) {
        setErrorMsg(t('img_err_mode_mismatch'))
        setPhase('error')
        return
      }
      const items = expectedKind === 'blocks' ? (json.blocks ?? []) : (json.tasks ?? [])
      if (items.length === 0) {
        setErrorMsg(t('img_no_tasks'))
        setPhase('error')
        return
      }
      const kind = expectedKind

      setResultKind(kind)
      setResults(items)
      setSummary(json.summary ?? '')
      setSelected(new Set(items.map((_, i) => i)))
      setPhase('results')
    } catch (e) {
      console.error('[ImageMode]', e)
      setErrorMsg(e.message === 'auth' ? t('img_err_auth') : t('img_err_analyze'))
      setPhase('error')
    }
  }

  const toggleSelect = (i) => setSelected(prev => {
    const next = new Set(prev); next.has(i) ? next.delete(i) : next.add(i); return next
  })

  const handleImport = async () => {
    setSaving(true)
    try {
      for (const i of selected) {
        const item = results[i]
        if (resultKind === 'blocks') {
          // Blocco: date + startTime + endTime → startAt/endAt in local time
          const date     = item.date
          const startTime = item.startTime
          let   endTime   = item.endTime
          if (!date || !startTime || !endTime) continue
          // Turno notturno: se endTime <= startTime, cade al giorno dopo
          let endDate = date
          if (endTime <= startTime) {
            const d = new Date(`${date}T00:00:00`)
            d.setDate(d.getDate() + 1)
            const pad = n => String(n).padStart(2, '0')
            endDate = `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}`
          }
          const startAt = partsToTs(date, startTime)
          const endAt   = partsToTs(endDate, endTime)
          const validType = BLOCK_TYPES[item.type] ? item.type : 'Lavoro'
          await addBlocco({
            type: validType,
            label: item.label ?? '',
            startAt, endAt,
            recurrence: 'once',
          })
        } else {
          const clean = stripPastDate({
            title: item.title, category: item.category, dueDate: item.dueDate ?? null,
            dueTime: item.dueTime ?? null, priority: item.priority, notes: item.notes ?? '',
            durationMinutes: item.duration_minutes ?? null,
          }, lang, t)
          const newId = await onSave(clean)
          // Smart list auto-create: se l'AI ha estratto voci di checklist per
          // questo task, crea la lista e popola gli item.
          const aiItems = Array.isArray(item.items)
            ? item.items.map(s => (typeof s === 'string' ? s.trim() : '')).filter(Boolean)
            : []
          if (newId && aiItems.length > 0) {
            try {
              const sls = useSmartListStore.getState()
              await sls.createList(newId)
              for (const text of aiItems.slice(0, 20)) {
                await sls.addItem(newId, { text })
              }
            } catch (e) { console.warn('[ImageMode] smart list create failed', e) }
          }
        }
      }
      onClose()
    } catch (e) {
      console.error('[ImageMode handleImport]', e)
      setSaving(false)
    }
  }

  const reset = () => { setPhase('upload'); setPreview(null); setResults([]); setErrorMsg('') }

  return (
    <div className="image-modal">

      {/* Hidden file inputs — always mounted so onChange fires even after phase changes */}
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={e => { if (e.target.files[0]) analyzeFile(e.target.files[0]); e.target.value = '' }}
      />
      <input
        ref={cameraRef}
        type="file"
        accept="image/*"
        capture="environment"
        style={{ display: 'none' }}
        onChange={e => { if (e.target.files[0]) analyzeFile(e.target.files[0]); e.target.value = '' }}
      />

      {phase === 'upload' && (
        <>
          <div
            className="drop-zone"
            onClick={() => inputRef.current?.click()}
            onDragOver={e  => { e.preventDefault(); e.currentTarget.classList.add('drag-over') }}
            onDragLeave={e => e.currentTarget.classList.remove('drag-over')}
            onDrop={e => { e.preventDefault(); e.currentTarget.classList.remove('drag-over'); analyzeFile(e.dataTransfer.files[0]) }}
          >
            <Icon.Document size={36} className="drop-icon" />
            <div className="drop-text">{t('img_title')}</div>
            <div className="drop-sub">{t('img_sub')}</div>
          </div>
          {/* Blocks mode toggle: quando ON, l'immagine viene interpretata come
              orario di lavoro / indisponibilità e finisce nella tabella blocchi. */}
          <div className="img-mode-toggle" onClick={() => setBlocksMode(v => !v)}>
            <span className="img-mode-icon">
              {blocksMode ? <Icon.Calendar size={20} /> : <Icon.Document size={20} />}
            </span>
            <div style={{ flex: 1 }}>
              <div className="img-mode-label">{t('img_blocks_mode')}</div>
              <div className="img-mode-sub">{blocksMode ? t('img_blocks_mode_on') : t('img_blocks_mode_off')}</div>
            </div>
            <div className={`toggle${blocksMode ? ' on' : ''}`} />
          </div>

          <div className="img-upload-actions">
            <button className="btn btn-ghost" onClick={() => cameraRef.current?.click()}>
              <Camera size={18} /> {t('img_take_photo')}
            </button>
            <button className="btn btn-ghost" onClick={() => inputRef.current?.click()}>
              <ImageIcon size={18} /> {t('img_choose_gallery')}
            </button>
          </div>
        </>
      )}

      {phase === 'analyzing' && (
        <div className="img-analyzing">
          {preview && <img src={preview} className="img-preview-thumb" alt="" />}
          <div className="voice-processing-spinner" />
          <div className="voice-title">{t('img_analyzing')}</div>
        </div>
      )}

      {phase === 'results' && editingIndex === null && (
        <>
          {summary && (
            <div className="img-summary">
              <Icon.Search size={14} /> {summary}
            </div>
          )}
          <p className="img-select-hint">
            {results.length === 1
              ? t('img_found_one', { n: results.length })
              : t('img_found_many', { n: results.length })}
          </p>
          <div className="extracted-tasks">
            {results.map((item, i) => {
              const sel = selected.has(i)
              if (resultKind === 'blocks') {
                const meta = BLOCK_TYPES[item.type] || BLOCK_TYPES.Lavoro
                return (
                  <div key={i} className={`extracted-task${sel ? ' selected' : ''}`} onClick={() => toggleSelect(i)}>
                    <input
                      type="checkbox"
                      className="extracted-checkbox"
                      checked={sel}
                      onChange={() => toggleSelect(i)}
                      onClick={e => e.stopPropagation()}
                    />
                    <span style={{ color: meta.fg, display: 'inline-flex' }}>
                      <meta.Icon size={18} strokeWidth={2} />
                    </span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '0.88rem', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {item.label || meta.label}
                      </div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-2)', marginTop: 2 }}>
                        {item.date}
                        {item.startTime ? ` · ${item.startTime}` : ''}
                        {item.endTime ? `–${item.endTime}` : ''}
                      </div>
                    </div>
                    <button
                      className="img-edit-btn"
                      title={t('img_edit_hint')}
                      onClick={e => { e.stopPropagation(); setEditingIndex(i) }}
                    >
                      <Edit3 size={13} />
                    </button>
                  </div>
                )
              }
              const cat = CATEGORIES[item.category]
              return (
                <div key={i} className={`extracted-task${sel ? ' selected' : ''}`} onClick={() => toggleSelect(i)}>
                  <input
                    type="checkbox"
                    className="extracted-checkbox"
                    checked={sel}
                    onChange={() => toggleSelect(i)}
                    onClick={e => e.stopPropagation()}
                  />
                  {(() => {
                    const CatIcon = cat?.Icon ?? CategoryIcon.altro
                    return (
                      <span style={{ color: cat?.color ?? 'var(--text-2)', display: 'inline-flex' }}>
                        <CatIcon size={18} strokeWidth={2} />
                      </span>
                    )
                  })()}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '0.88rem', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {item.title}
                    </div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-2)', marginTop: 2 }}>
                      {cat?.label}
                      {item.dueDate ? ` · ${item.dueDate}` : ''}
                      {item.notes   ? ` · ${item.notes.slice(0, 36)}${item.notes.length > 36 ? '…' : ''}` : ''}
                    </div>
                  </div>
                  <button
                    className="img-edit-btn"
                    title={t('img_edit_hint')}
                    onClick={e => { e.stopPropagation(); setEditingIndex(i) }}
                  >
                    <Edit3 size={13} />
                  </button>
                </div>
              )
            })}
          </div>
          <button className="btn btn-primary" onClick={handleImport} disabled={selected.size === 0 || saving}>
            {saving ? '…' : selected.size > 0 ? t('img_import_n', { n: selected.size }) : t('img_import')}
          </button>
          <button className="btn btn-ghost" style={{ marginTop: 8, width: '100%' }} onClick={reset}>
            {t('img_new_analysis')}
          </button>
        </>
      )}

      {phase === 'results' && editingIndex !== null && resultKind === 'tasks' && (
        <ManualForm
          initialData={stripPastDate(results[editingIndex], lang, t)}
          onSave={async (updatedData) => {
            // Save changes back to results array, don't actually create the task
            setResults(prev => prev.map((r, i) => i === editingIndex ? { ...r, ...updatedData } : r))
            setEditingIndex(null)
          }}
          onClose={() => setEditingIndex(null)}
        />
      )}

      {editingIndex !== null && resultKind === 'blocks' && (() => {
        const item = results[editingIndex]
        // Turno notturno: endTime <= startTime → end cade il giorno dopo
        let endDateISO = item.date
        if (item.startTime && item.endTime && item.endTime <= item.startTime) {
          const d = new Date(`${item.date}T00:00:00`)
          d.setDate(d.getDate() + 1)
          const pad = n => String(n).padStart(2, '0')
          endDateISO = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
        }
        const synthEditing = {
          id:        `__batch_${editingIndex}`, // dummy — bypassed by onSaveOverride
          type:      item.type  || 'Lavoro',
          label:     item.label || '',
          startAt:   partsToTs(item.date, item.startTime || '08:00'),
          endAt:     partsToTs(endDateISO, item.endTime || '17:00'),
          recurrence: 'once',
        }
        return (
          <BloccoForm
            editing={synthEditing}
            onClose={() => setEditingIndex(null)}
            onSaveOverride={({ type, label, startAt, endAt }) => {
              const s = new Date(startAt)
              const e = new Date(endAt)
              const pad = n => String(n).padStart(2, '0')
              const date      = `${s.getFullYear()}-${pad(s.getMonth() + 1)}-${pad(s.getDate())}`
              const startTime = `${pad(s.getHours())}:${pad(s.getMinutes())}`
              const endTime   = `${pad(e.getHours())}:${pad(e.getMinutes())}`
              setResults(prev => prev.map((r, i) =>
                i === editingIndex ? { ...r, type, label, date, startTime, endTime } : r
              ))
              setEditingIndex(null)
            }}
          />
        )
      })()}

      {phase === 'error' && (
        <>
          <div style={{ fontSize: '2.5rem', marginBottom: 12 }}>⚠️</div>
          <div className="voice-title" style={{ color: 'var(--danger, #e74c3c)' }}>
            {t('img_error')}
          </div>
          <div className="voice-subtitle" style={{ textAlign: 'center' }}>{errorMsg}</div>
          <button className="btn btn-primary" onClick={reset} style={{ marginTop: 24, width: '100%' }}>
            {t('img_retry')}
          </button>
        </>
      )}

    </div>
  )
}
